import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronUp, Loader2, CheckCircle2, XCircle, Clock, Eye } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

type JobStatus =
  | "uploaded"
  | "extracting"
  | "analysing"
  | "draft_ready"
  | "reviewed"
  | "published"
  | "failed"
  | "cancelled";

interface AiUploadJob {
  id: string;
  status: JobStatus;
  totalFiles: number;
  targetPathwayId: string | null;
  targetPathwayTitle?: string | null;
  createdAt: string;
  completedAt: string | null;
}

function statusBadge(status: JobStatus) {
  switch (status) {
    case "uploaded":
    case "extracting":
    case "analysing":
      return (
        <Badge variant="secondary" className="text-[10px] gap-1">
          <Loader2 className="h-2.5 w-2.5 animate-spin" />
          Processing
        </Badge>
      );
    case "draft_ready":
    case "reviewed":
      return (
        <Badge className="text-[10px] bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 gap-1 hover:bg-amber-100">
          <Eye className="h-2.5 w-2.5" />
          Ready to review
        </Badge>
      );
    case "published":
      return (
        <Badge className="text-[10px] bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300 gap-1 hover:bg-green-100">
          <CheckCircle2 className="h-2.5 w-2.5" />
          Published
        </Badge>
      );
    case "failed":
      return (
        <Badge variant="destructive" className="text-[10px] gap-1">
          <XCircle className="h-2.5 w-2.5" />
          Failed
        </Badge>
      );
    case "cancelled":
      return (
        <Badge variant="outline" className="text-[10px] text-muted-foreground">
          Cancelled
        </Badge>
      );
  }
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function AIUploadJobList() {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(true);

  const { data, isLoading } = useQuery<{ jobs: AiUploadJob[] }>({
    queryKey: ["/api/creator/ai-upload/jobs"],
    staleTime: 30_000,
    refetchInterval: (query) => {
      const jobs = (query.state.data as any)?.jobs ?? [];
      const hasActive = jobs.some((j: AiUploadJob) =>
        ["uploaded", "extracting", "analysing"].includes(j.status)
      );
      return hasActive ? 10_000 : false;
    },
  });

  const jobs = data?.jobs ?? [];

  if (!isLoading && jobs.length === 0) return null;

  return (
    <div className="border border-border rounded-lg overflow-hidden" data-testid="ai-upload-job-list">
      <button
        className="w-full flex items-center justify-between px-4 py-3 bg-muted/30 hover:bg-muted/50 transition-colors text-sm font-medium"
        onClick={() => setExpanded((p) => !p)}
        data-testid="button-toggle-job-list"
      >
        <span className="flex items-center gap-2">
          Recent AI upload jobs
          {!isLoading && jobs.length > 0 && (
            <Badge variant="secondary" className="text-[10px] h-4 px-1.5">
              {jobs.length}
            </Badge>
          )}
        </span>
        {expanded ? (
          <ChevronUp className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        )}
      </button>

      {expanded && (
        <div className="divide-y divide-border">
          {isLoading ? (
            <div className="p-4 space-y-2">
              {[...Array(2)].map((_, i) => (
                <Skeleton key={i} className="h-10 rounded" />
              ))}
            </div>
          ) : (
            jobs.map((job) => (
              <div
                key={job.id}
                className="flex items-center gap-3 px-4 py-3 text-sm"
                data-testid={`job-row-${job.id}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate text-foreground">
                    {job.targetPathwayTitle ?? "New pathway from uploads"}
                  </p>
                  <p className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5">
                    <Clock className="h-3 w-3" />
                    {formatDate(job.createdAt)}
                    {" · "}
                    {job.totalFiles} file{job.totalFiles !== 1 ? "s" : ""}
                  </p>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  {statusBadge(job.status)}
                  {(job.status === "draft_ready" || job.status === "reviewed") && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs"
                      onClick={() => navigate(`/creator/ai-upload/${job.id}/review`)}
                      data-testid={`button-review-draft-${job.id}`}
                    >
                      Review Draft
                    </Button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
